/**
 * store/queue.ts — the «очередь» slice: which tracks play one after another, in what order,
 * and which of them is playing.
 *
 * Task 08. `spec.md` «Плеер»: «Очередь: локальная с сохранением на сервере (переживает
 * перезапуск и смену телефона), перемешивание с сохранённым „зерном“ (порядок не меняется при
 * перезагрузке), повтор одного и всего списка». The queue lives here while the app is open;
 * `engine/sync.ts` leaves a copy on the server and finds it again on the next launch.
 *
 * Its own store, like `store/library.ts`: it imports neither the player's slice nor the
 * library's, and neither of them imports it. Four position updates a second touch the player
 * and nobody here; a page of three thousand rows touches the library and nobody here; an edit
 * to the queue touches only what draws the queue. `engine/player.ts` is the one module that
 * reads all three.
 *
 * ## The entries are always in the listener's order
 *
 * The shuffled order is never stored: it is {@link playOrder}, computed from the entries, the
 * 32-bit {@link QueueState.seed} and the {@link QueueState.anchor}. So the same queue plays
 * the same way after a reload and on another phone — the server keeps three numbers, not a
 * second list — and turning shuffle off needs no memory of what the order used to be.
 *
 * The permutation is a Fisher–Yates walk driven by `mulberry32`, carried over from the old
 * project: `Math.random` would give every launch its own order, which is exactly what the
 * checklist forbids.
 *
 * ## A shuffled pass starts at the anchor
 *
 * `playOrder` puts the anchor first and shuffles everything else after it. The anchor is the
 * current track at the moment shuffle is turned on, so nothing ahead is skipped and nothing
 * repeats before the pass is over — a plain permutation of the whole queue would drop the
 * current track somewhere in the middle, and without «repeat» everything before it would
 * never play.
 *
 * The permutation is over positions, so an edit — a track added, removed or moved — changes
 * it anyway. Every edit therefore anchors afresh on the current track: the track playing stays
 * the first of the pass and every other track is ahead of it again. The price, written down:
 * after an edit a track already heard in this pass can come round once more.
 *
 * ## The track playing stays in the queue
 *
 * `remove` refuses the current entry and `clear` keeps it: the player holds exactly the
 * current entry, and a queue that no longer contains what is playing would leave «следующий»
 * with nothing to count from. To skip a track, the listener presses «следующий».
 *
 * ## «Играть следующим» (task 44)
 *
 * {@link QueueState.insertNext} puts a track right after the current one in the listener's
 * order, and {@link QueueState.pinned} counts such tracks: the `pinned` entries right after the
 * pass's start — the anchor while shuffled, the current entry otherwise — play in the listener's
 * order before everything else. So under shuffle the pass is: the anchor, the pinned tracks in
 * the order they were put there, then the rest shuffled. Moving along the pass does not touch a
 * shuffled `pinned` — the pass stays the one it was; unshuffled, each step onto a pinned track
 * leaves one fewer ahead. An edit anchors afresh, and the pinned tracks still to play are found
 * again by their keys right after the new start: a pinned row dragged away or removed simply is
 * no longer pinned. The server keeps the number (`pinned` of `PUT /api/queue`).
 *
 * ## The list it came from (task 44)
 *
 * {@link QueueState.source} — the list's key (`store/library.ts`, `listKey`) and the label of its
 * next page — lets the player go on down the list past the rows that were loaded when it was
 * tapped (`engine/player.ts`, «The queue goes on down its list»); {@link QueueState.appendPage}
 * adds a page and moves the label on. Kept on the server too, so a queue built today goes on
 * tomorrow.
 *
 * A page is an edit like any other, and under shuffle an edit anchors afresh — which, for a page,
 * would put every track heard so far back among the ones ahead: the review of task 44 counted
 * seven of eight. So a shuffled page is a pass of its own: the player hands it in on the last
 * track of the pass (nothing of the old pass is left to hear), and {@link QueueState.freshFrom}
 * marks where it starts. The positions below it — but for the anchor and the pinned run — were
 * heard: `playOrder` puts them before the anchor, so «следующий» goes into the new page and
 * «предыдущий» back into what was heard, and «повтор всего» goes round the whole list. Every
 * other edit sets it back to 0, the price the paragraph above already pays.
 */

import { create } from 'zustand';

import type { RepeatMode, TrackItem, TransitionMode } from '../lib/api';

export type { RepeatMode, TransitionMode };

/* ────────────────────────────────────────────────────────────────────────────
 * Shapes
 * ──────────────────────────────────────────────────────────────────────────── */

/** The order a tap on «Повтор» walks. */
export const REPEAT_MODES: readonly RepeatMode[] = Object.freeze(['off', 'all', 'one']);

/**
 * Task 45: the order a tap on «Переход» walks. The way is the listener's, not the list's: a new
 * queue keeps it, like «Повтор».
 */
export const TRANSITION_MODES: readonly TransitionMode[] = Object.freeze(['cut', 'blend', 'mix']);

/**
 * The longest queue: a day of music. The server refuses more (`routes/queue.ts`), and a tap
 * in a long list builds the queue from the tapped row down to this many.
 */
export const QUEUE_MAX_TRACKS = 500;

/** One place in the queue. `key` is this place's own: a track may stand in a queue twice. */
export interface QueueEntry {
  readonly key: number;
  readonly track: TrackItem;
}

/** Task 44: the list a queue was built from, and the label of its next page. */
export interface QueueSource {
  /** `listKey` of `store/library.ts`. */
  readonly key: string;
  /** `nextCursor` of the list's last page taken, as the server gave it. */
  readonly cursor: string;
}

/** A queue as the server hands it back (`GET /api/queue`), and what {@link hydrate} takes. */
export interface RestoredQueue {
  readonly items: readonly TrackItem[];
  readonly index: number;
  readonly shuffle: boolean;
  readonly shuffleSeed: number;
  readonly shuffleAnchor: number;
  readonly repeat: RepeatMode;
  readonly positionS: number;
  /** Task 44. Absent from a server before task 44: no source, nothing pinned, nothing heard. */
  readonly sourceKey?: string | null | undefined;
  readonly sourceCursor?: string | null | undefined;
  readonly pinned?: number | undefined;
  readonly freshFrom?: number | undefined;
  /** Task 45. Absent from a server before task 45: `blend`, the default. */
  readonly transition?: TransitionMode | undefined;
}

export interface QueueState {
  /** In the listener's order, always. The shuffled order is {@link playOrder}. */
  readonly entries: readonly QueueEntry[];
  /** Position of the current entry; -1 for an empty queue. */
  readonly index: number;
  readonly shuffle: boolean;
  /** 32-bit, unsigned. The whole of the shuffled order, together with the anchor. */
  readonly seed: number;
  /** Position the shuffled pass starts from; -1 when shuffle is off. */
  readonly anchor: number;
  readonly repeat: RepeatMode;
  /** Task 45: how one track goes into the next (`engine/player.ts`, «Two decks»). */
  readonly transition: TransitionMode;
  /**
   * Bumped by every change the listener makes, and by nothing else. `engine/sync.ts` saves
   * when it moves — and never before it has moved or the saved queue has been read, so an
   * empty queue at launch cannot overwrite the one on the server.
   */
  readonly revision: number;
  /** Task 44: see ««Играть следующим»» in the header. */
  readonly pinned: number;
  /** Task 44: see «The list it came from». `null` — nothing more to take from anywhere. */
  readonly source: QueueSource | null;
  /**
   * Task 44: under shuffle, the positions below it — but the anchor and the pinned run — were
   * heard earlier in this pass and play before the anchor. 0 — none; see «The list it came from».
   */
  readonly freshFrom: number;

  /**
   * A new queue: `tracks` in order, the first one current. A tap in a list; `source` is that
   * list, when it has more pages than were loaded.
   */
  readonly replace: (tracks: readonly TrackItem[], source?: QueueSource | null) => void;
  /** Appends `track`. `false` when the queue is full. */
  readonly enqueue: (track: TrackItem) => boolean;
  /** Task 44: puts `track` right after the current one, pinned. `false` when the queue is full. */
  readonly insertNext: (track: TrackItem) => boolean;
  /**
   * Task 44: appends the next page of the source, up to the queue's limit, and moves the label
   * on to `cursor` (`null` — the list is over). Returns how many tracks went in. Shuffled, the
   * page is a pass of its own, and everything before it counts as heard — so it is handed in on
   * the pass's last track (`engine/player.ts`).
   */
  readonly appendPage: (tracks: readonly TrackItem[], cursor: string | null) => number;
  /** Takes the entry out. Not the current one: see the header. */
  readonly remove: (key: number) => void;
  /** Puts the entry at position `to`, clamped to the queue. What plays does not change. */
  readonly move: (key: number, to: number) => void;
  /** Everything but the track playing. */
  readonly clear: () => void;
  /** Makes the entry at `index` current. */
  readonly setIndex: (index: number) => void;
  readonly toggleShuffle: () => void;
  readonly cycleRepeat: () => void;
  /** Task 45: the next of {@link TRANSITION_MODES}. */
  readonly cycleTransition: () => void;
  /**
   * Adopts the saved queue. Not a change of the listener's: the revision stays. Returns the
   * second to hold the current track at, or `undefined` when the saved queue has none.
   */
  readonly hydrate: (restored: RestoredQueue) => number | undefined;
}

/* ────────────────────────────────────────────────────────────────────────────
 * The seeded shuffle
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * `mulberry32` — a 32-bit-seeded generator, identical in every JavaScript engine: every step
 * goes through `Math.imul` and `>>>`, so no float or 64-bit difference can creep in. Quality
 * is beside the point; reproducibility across phones is the whole requirement.
 */
function mulberry32(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4_294_967_296;
  };
}

/**
 * A deterministic Fisher–Yates: a new array, the input untouched. The backwards walk with
 * `j = floor(random() * (i + 1))` is the variant that gives a uniform permutation;
 * `sort(() => random() - 0.5)` is neither uniform nor the same in two engines.
 */
export function seededShuffle<T>(input: readonly T[], seed: number): T[] {
  const out = [...input];
  const random = mulberry32(seed);
  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = Math.floor(random() * (i + 1));
    const a = out[i];
    const b = out[j];
    // `noUncheckedIndexedAccess`: both are in range, but the compiler cannot know it.
    if (a === undefined || b === undefined) continue;
    out[i] = b;
    out[j] = a;
  }
  return out;
}

function cryptoSeed(): number {
  const values = new Uint32Array(1);
  globalThis.crypto.getRandomValues(values);
  return values[0] ?? 0;
}

let seedSource: () => number = cryptoSeed;

/**
 * Replaces where a new seed comes from — the web suite's seam, so a test can know the order
 * it will hear. `undefined` puts the real one back.
 */
export function setSeedSource(source: (() => number) | undefined): void {
  seedSource = source ?? cryptoSeed;
}

/* ────────────────────────────────────────────────────────────────────────────
 * What plays next
 * ──────────────────────────────────────────────────────────────────────────── */

type Ordering = Pick<
  QueueState,
  'entries' | 'index' | 'shuffle' | 'seed' | 'anchor' | 'repeat' | 'pinned' | 'freshFrom'
>;

/**
 * Positions in the order they play: the entries' order, or the shuffled pass — what was heard
 * before the anchor (task 44, `freshFrom`), the anchor, the pinned entries after it in the
 * listener's order (task 44), the rest shuffled.
 */
export function playOrder(
  queue: Omit<Ordering, 'index' | 'repeat' | 'pinned' | 'freshFrom'> & {
    readonly pinned?: number;
    readonly freshFrom?: number;
  },
): number[] {
  const all = queue.entries.map((_, position) => position);
  if (!queue.shuffle) return all;
  const anchor = queue.anchor >= 0 && queue.anchor < all.length ? queue.anchor : -1;
  if (anchor === -1) return seededShuffle(all, queue.seed);
  const last = Math.min(anchor + Math.max(0, queue.pinned ?? 0), all.length - 1);
  const pinned = all.slice(anchor + 1, last + 1);
  const rest = all.filter(
    (position) => position !== anchor && (position <= anchor || position > last),
  );
  const freshFrom = queue.freshFrom ?? 0;
  return [
    ...seededShuffle(
      rest.filter((position) => position < freshFrom),
      queue.seed,
    ),
    anchor,
    ...pinned,
    ...seededShuffle(
      rest.filter((position) => position >= freshFrom),
      queue.seed,
    ),
  ];
}

/** Task 44: how many tracks are left to play after the current one in this pass. */
export function passLeft(queue: Omit<Ordering, 'repeat'>): number {
  const order = playOrder(queue);
  return order.length - 1 - order.indexOf(queue.index);
}

/**
 * Task 44: the keys of the pinned entries still to play, in order. Shuffled, the pinned run
 * follows the anchor, and a current entry past the run has left nothing of it ahead.
 */
export function pinsAhead(queue: Ordering): number[] {
  const base = queue.shuffle ? queue.anchor : queue.index;
  if (base < 0 || queue.pinned <= 0) return [];
  const first = base + 1;
  const last = Math.min(base + queue.pinned, queue.entries.length - 1);
  if (queue.shuffle && queue.index !== base && (queue.index < first || queue.index > last)) {
    return [];
  }
  const from = Math.max(first, queue.index + 1);
  return queue.entries.slice(from, last + 1).map((entry) => entry.key);
}

/** How many entries right after `index` are among `keys`, unbroken. */
function pinnedAfter(
  entries: readonly QueueEntry[],
  index: number,
  keys: ReadonlySet<number>,
): number {
  let count = 0;
  for (let at = index + 1; at < entries.length; at += 1) {
    const entry = entries[at];
    if (entry === undefined || !keys.has(entry.key)) break;
    count += 1;
  }
  return count;
}

/**
 * The position that plays after the current one, or -1 when the queue is over.
 *
 * `auto` is the end of a track, `manual` is a button. They differ under «repeat one» only:
 * the end of the track plays it again, the button moves on — a listener who presses
 * «следующий» wants the next track, whatever the mode — and wraps as under «repeat all».
 */
export function nextIndex(queue: Ordering, how: 'auto' | 'manual'): number {
  if (queue.index < 0 || queue.index >= queue.entries.length) return -1;
  if (how === 'auto' && queue.repeat === 'one') return queue.index;
  const order = playOrder(queue);
  const after = order[order.indexOf(queue.index) + 1];
  if (after !== undefined) return after;
  return queue.repeat === 'off' ? -1 : (order[0] ?? -1);
}

/** The position before the current one in the order they play, or -1 at the start. */
export function previousIndex(queue: Ordering): number {
  if (queue.index < 0 || queue.index >= queue.entries.length) return -1;
  const order = playOrder(queue);
  const at = order.indexOf(queue.index);
  if (at > 0) return order[at - 1] ?? -1;
  return queue.repeat === 'off' ? -1 : (order.at(-1) ?? -1);
}

/* ────────────────────────────────────────────────────────────────────────────
 * The store
 * ──────────────────────────────────────────────────────────────────────────── */

/** Keys count up for the life of the page: an entry's key never names another entry. */
let lastKey = 0;

function entryOf(track: TrackItem): QueueEntry {
  lastKey += 1;
  return { key: lastKey, track };
}

export const useQueue = create<QueueState>((set, get) => {
  /**
   * An edit of the listener's: the new state, re-anchored if shuffled, one revision on. The
   * pinned entries still to play are found again by their keys after the new start, unless the
   * patch says how many there are; nothing counts as heard, unless the patch says so.
   */
  const edit = (patch: Pick<QueueState, 'entries' | 'index'> & Partial<QueueState>): void => {
    const shuffle = patch.shuffle ?? get().shuffle;
    const pinned =
      patch.pinned ?? pinnedAfter(patch.entries, patch.index, new Set(pinsAhead(get())));
    set({
      ...patch,
      pinned,
      anchor: shuffle ? patch.index : -1,
      freshFrom: shuffle ? (patch.freshFrom ?? 0) : 0,
      revision: get().revision + 1,
    });
  };

  return {
    entries: [],
    index: -1,
    shuffle: false,
    seed: 0,
    anchor: -1,
    repeat: 'off',
    transition: 'blend',
    revision: 0,
    pinned: 0,
    source: null,
    freshFrom: 0,

    replace: (tracks, source = null) => {
      const entries = tracks.slice(0, QUEUE_MAX_TRACKS).map(entryOf);
      edit({
        entries,
        index: entries.length > 0 ? 0 : -1,
        pinned: 0,
        // A list cut at the limit has rows between the cut and its next page: no going on.
        source: tracks.length > QUEUE_MAX_TRACKS ? null : source,
        // A new list is a new pass: the tapped track first, the rest in a fresh order.
        ...(get().shuffle ? { seed: seedSource() >>> 0 } : {}),
      });
    },

    insertNext: (track) => {
      const { entries, index } = get();
      if (entries.length >= QUEUE_MAX_TRACKS) return false;
      if (index === -1) {
        edit({ entries: [entryOf(track)], index: 0, pinned: 0 });
        return true;
      }
      const ahead = new Set(pinsAhead(get()));
      const next = [...entries.slice(0, index + 1), entryOf(track), ...entries.slice(index + 1)];
      edit({ entries: next, index, pinned: 1 + pinnedAfter(next, index + 1, ahead) });
      return true;
    },

    appendPage: (tracks, cursor) => {
      const { entries, index, source } = get();
      if (source === null) return 0;
      const room = QUEUE_MAX_TRACKS - entries.length;
      const taken = tracks.slice(0, Math.max(0, room));
      const full =
        taken.length < tracks.length || entries.length + taken.length >= QUEUE_MAX_TRACKS;
      edit({
        entries: [...entries, ...taken.map(entryOf)],
        index,
        source: cursor === null || full ? null : { key: source.key, cursor },
        // Shuffled, everything already in the queue was heard: see «The list it came from».
        freshFrom: entries.length,
      });
      return taken.length;
    },

    enqueue: (track) => {
      const { entries, index } = get();
      if (entries.length >= QUEUE_MAX_TRACKS) return false;
      edit({ entries: [...entries, entryOf(track)], index: index === -1 ? 0 : index });
      return true;
    },

    remove: (key) => {
      const { entries, index } = get();
      const at = entries.findIndex((entry) => entry.key === key);
      if (at === -1 || at === index) return;
      edit({
        entries: entries.filter((entry) => entry.key !== key),
        index: at < index ? index - 1 : index,
      });
    },

    move: (key, to) => {
      const { entries, index } = get();
      const from = entries.findIndex((entry) => entry.key === key);
      const moved = entries[from];
      if (moved === undefined) return;
      const target = Math.min(Math.max(0, Math.round(to)), entries.length - 1);
      if (target === from) return;
      const currentKey = entries[index]?.key;
      const rest = entries.filter((entry) => entry.key !== key);
      const next = [...rest.slice(0, target), moved, ...rest.slice(target)];
      // The index follows the track, not the slot: a drag never changes what is playing.
      edit({ entries: next, index: next.findIndex((entry) => entry.key === currentKey) });
    },

    clear: () => {
      const { entries, index } = get();
      const current = entries[index];
      if (current === undefined) {
        if (entries.length > 0) edit({ entries: [], index: -1 });
        return;
      }
      if (entries.length === 1) return;
      edit({ entries: [current], index: 0 });
    },

    setIndex: (index) => {
      const { entries, shuffle } = get();
      if (index < 0 || index >= entries.length || index === get().index) return;
      // Moving through the pass is not an edit of it: the anchor stays where the pass began,
      // and so does a shuffled pinned run. Unshuffled, the pinned entries are counted from the
      // current one, so a step onto one of them leaves one fewer ahead.
      const pinned = shuffle
        ? get().pinned
        : pinnedAfter(entries, index, new Set(pinsAhead(get())));
      set({ index, pinned, revision: get().revision + 1 });
    },

    toggleShuffle: () => {
      const { shuffle, index, entries } = get();
      set(
        shuffle
          ? {
              shuffle: false,
              anchor: -1,
              // From now on counted from the current entry: what is left of the run ahead of it.
              pinned: pinnedAfter(entries, index, new Set(pinsAhead(get()))),
              freshFrom: 0,
              revision: get().revision + 1,
            }
          : {
              shuffle: true,
              seed: seedSource() >>> 0,
              anchor: index,
              freshFrom: 0,
              revision: get().revision + 1,
            },
      );
    },

    cycleRepeat: () => {
      const at = REPEAT_MODES.indexOf(get().repeat);
      set({
        repeat: REPEAT_MODES[(at + 1) % REPEAT_MODES.length] ?? 'off',
        revision: get().revision + 1,
      });
    },

    cycleTransition: () => {
      const at = TRANSITION_MODES.indexOf(get().transition);
      set({
        transition: TRANSITION_MODES[(at + 1) % TRANSITION_MODES.length] ?? 'blend',
        revision: get().revision + 1,
      });
    },

    hydrate: (restored) => {
      const entries = restored.items.slice(0, QUEUE_MAX_TRACKS).map(entryOf);
      const valid = restored.index >= 0 && restored.index < entries.length;
      const index = valid ? restored.index : entries.length > 0 ? 0 : -1;
      const anchorValid = restored.shuffleAnchor >= -1 && restored.shuffleAnchor < entries.length;
      const anchor = anchorValid ? restored.shuffleAnchor : restored.shuffle ? index : -1;
      const base = restored.shuffle ? anchor : index;
      const pinned = Math.max(0, Math.min(restored.pinned ?? 0, entries.length - 1 - base));
      const source =
        typeof restored.sourceKey === 'string' && typeof restored.sourceCursor === 'string'
          ? { key: restored.sourceKey, cursor: restored.sourceCursor }
          : null;
      const freshFrom = Math.max(0, Math.min(restored.freshFrom ?? 0, entries.length));
      set({
        entries,
        index,
        shuffle: restored.shuffle,
        seed: restored.shuffleSeed >>> 0,
        anchor,
        repeat: restored.repeat,
        transition: restored.transition ?? 'blend',
        pinned: base < 0 ? 0 : pinned,
        source,
        freshFrom: restored.shuffle && anchor >= 0 ? freshFrom : 0,
      });
      if (entries[index] === undefined) return undefined;
      // A second saved against another index is not this track's.
      return valid ? Math.max(0, restored.positionS) : 0;
    },
  };
});
